import React from 'react';
import { GitFork } from '@phosphor-icons/react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Heading,
  TagBadge,
  Text,
} from '@chatsdet/ui';

export type ConvergenceDriftTier = 'allowed' | 'warning' | 'hard';
export type ConvergenceDriftClassification = 'superficial' | 'semantic' | 'uncertain' | null;

export type ConvergenceMergePreview = {
  canMerge: boolean;
  driftAssessment: {
    tier: ConvergenceDriftTier;
    score: number;
    hardReasons: string[];
    classification: ConvergenceDriftClassification;
    reasoning: string | null;
    changes: Array<{ nodeId: string; field: string; before: unknown; after: unknown }>;
    executionModeChanges?: Array<{ nodeId: string; field: string; before: unknown; after: unknown }>;
  };
  expectedDriftHash: string;
  expectedOriginUpdatedAt: string;
  expectedForkUpdatedAt: string;
};

export type WorkflowForkLineage = {
  originName: string | null;
  onOpenOrigin: () => void;
  onSuggestToOriginal: () => void;
  isSuggestingToOriginal: boolean;
  convergencePreview?: ConvergenceMergePreview | null;
  convergencePreviewLoading?: boolean;
  onMergeToOriginal?: () => void;
  isMergingToOriginal?: boolean;
};

export function ForkOriginLink({ lineage }: { lineage: WorkflowForkLineage }) {
  return (
    <button
      type="button"
      onClick={lineage.onOpenOrigin}
      title="Open the flow this was forked from"
      className="inline-flex min-w-0 max-w-[14rem] items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
    >
      <GitFork size={12} className="flex-shrink-0" />
      <span className="truncate">Forked from {lineage.originName ?? 'original'}</span>
    </button>
  );
}

function formatChangeValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === undefined) return '—';
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

function tierLabel(tier: ConvergenceDriftTier): string {
  return tier === 'hard' ? 'Hard gate' : tier === 'warning' ? 'Warning' : 'Allowed';
}

function tierColor(tier: ConvergenceDriftTier): 'blue' | 'yellow' | 'red' {
  return tier === 'hard' ? 'red' : tier === 'warning' ? 'yellow' : 'blue';
}

/** Which fork action the fork menu offers: merge once a convergence preview exists, else suggest. */
export function getForkLineageAction(
  lineage: WorkflowForkLineage
): 'merge' | 'suggest' | null {
  if (lineage.convergencePreviewLoading) return null;
  if (lineage.convergencePreview) return lineage.onMergeToOriginal ? 'merge' : null;
  return 'suggest';
}

/** The merge is blocked when the API disallows it or one is already in flight. */
export function isForkMergeDisabled(lineage: WorkflowForkLineage): boolean {
  return !lineage.convergencePreview?.canMerge || !!lineage.isMergingToOriginal;
}

export function ForkMergeDialog({
  lineage,
  isOpen,
  onOpenChange: setOpen,
}: {
  lineage: WorkflowForkLineage;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const preview = lineage.convergencePreview;

  if (!preview || !lineage.onMergeToOriginal) return null;

  const { driftAssessment } = preview;
  const executionModeChanges = driftAssessment.executionModeChanges ?? [];
  const isDisabled = isForkMergeDisabled(lineage);

  return (
    <>
      <DialogTrigger isOpen={isOpen} onOpenChange={setOpen}>
        <Button className="hidden">Review convergence merge</Button>
        <Dialog>
          <DialogContent>
            <Heading slot="title">Merge optimization into original?</Heading>
            <Text className="text-sm">
              Review the detected drift before replacing the original flow&apos;s behavioral
              definition with this verified fork.
            </Text>

            <div className="flex flex-wrap items-center gap-2 text-sm">
              <TagBadge
                name={tierLabel(driftAssessment.tier)}
                color={tierColor(driftAssessment.tier)}
                hideDot
              />
              <span className="text-muted-foreground">
                Drift score{' '}
                <strong className="font-semibold tabular-nums">{driftAssessment.score}</strong>
              </span>
            </div>

            {driftAssessment.hardReasons.length > 0 && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm dark:border-red-900/60 dark:bg-red-950/30">
                <Text className="font-medium">Hard-gate reasons</Text>
                <ul className="mt-1 list-disc space-y-1 pl-5 text-muted-foreground">
                  {driftAssessment.hardReasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="space-y-1 text-sm">
              <Text>
                Agent assessment:{' '}
                <strong className="font-semibold">
                  {driftAssessment.classification ?? 'Not classified'}
                </strong>
              </Text>
              {driftAssessment.reasoning && (
                <Text className="text-muted-foreground">{driftAssessment.reasoning}</Text>
              )}
            </div>

            {driftAssessment.changes.length > 0 && (
              <div className="space-y-1 text-sm">
                <Text className="font-medium">Detected changes</Text>
                <ul className="max-h-32 space-y-1 overflow-y-auto border-y border-border px-3 py-2 text-muted-foreground">
                  {driftAssessment.changes.map((change, index) => (
                    <li key={`${change.nodeId}-${change.field}-${index}`}>
                      <span className="font-medium text-foreground">{change.field}</span> on{' '}
                      {change.nodeId}: {formatChangeValue(change.before)} →{' '}
                      {formatChangeValue(change.after)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {executionModeChanges.length > 0 && (
              <div className="space-y-1 text-sm">
                <Text className="font-medium">Execution-mode changes (not part of the drift tier)</Text>
                <ul className="max-h-32 space-y-1 overflow-y-auto border-y border-border px-3 py-2 text-muted-foreground">
                  {executionModeChanges.map((change, index) => (
                    <li key={`${change.nodeId}-${change.field}-${index}`}>
                      <span className="font-medium text-foreground">{change.field}</span> on{' '}
                      {change.nodeId}: {formatChangeValue(change.before)} →{' '}
                      {formatChangeValue(change.after)}
                      {change.field === 'alwaysUseAgent' && change.after === true
                        ? ' — this node will run with the agent, not the cache'
                        : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <DialogFooter>
              <Button variant="secondary" onPress={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onPress={() => {
                  setOpen(false);
                  lineage.onMergeToOriginal?.();
                }}
                isDisabled={isDisabled}
              >
                Merge into original
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogTrigger>
    </>
  );
}
